/**
 * PremiumVideoItem
 * Flutter source: wallet_screen.dart - _premiumVideoItem()
 *
 * Displays premium video payment transactions.
 */
import React from 'react';
import type { AppTransactionEntity } from '@/domain/entities/transaction/appTransactionEntity';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { ContentPreview } from '@/presentation/feature/content/components/ContentPreview';
import { useDateFormatters } from '@/presentation/utils/useDateFormatters';
import { env } from '@/config/env';
import { AdminUserAvatar } from './AdminUserAvatar';

interface PremiumVideoItemProps {
  transaction: AppTransactionEntity;
  currentUserId: string;
  onContentTap: (transaction: AppTransactionEntity) => void;
  isFromAdmin?: boolean;
}

export const PremiumVideoItem: React.FC<PremiumVideoItemProps> = ({
  transaction,
  currentUserId,
  onContentTap,
  isFromAdmin = false,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const { l10n } = useLocale();
  const dateFormatters = useDateFormatters();

  const who = transaction.usersInfo.userId === currentUserId ? 'You' : `${transaction.usersInfo.name} `;
  const title = `${who} ${l10n.wallet.paidPremiumVideo}`;

  const thumbnailUrl = transaction.mediaInfo
    ? `${env.CDN_NAME}${transaction.mediaInfo.thumbnail}`
    : null;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: responsive.gap.sm }}>
      <div
        style={{
          width: responsive.avatar.medium,
          height: responsive.avatar.medium,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <AssetIcon
          path="/assets/images/premium_content.png"
          width={responsive.icon.medium}
          height={responsive.icon.medium}
        />
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: theme.text.primary, fontSize: responsive.font.sm, fontWeight: responsive.fontWeight.medium }}>
          {title}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: responsive.gap.xs, marginTop: responsive.gap.xs }}>
          <span style={{ color: theme.text.secondary, fontSize: responsive.font.xs }}>
            {transaction.mediaInfo?.premiumVideoCharge ?? 0}
          </span>
          <AssetIcon
            path="/assets/images/coins.png"
            width={responsive.icon.small}
            height={responsive.icon.small}
            color={theme.exclusive}
          />
          <span style={{ color: theme.text.secondary, fontSize: responsive.font.xs }}>
            · {dateFormatters.formatDate(transaction.createdDate, true)}
          </span>
        </div>
      </div>

      {thumbnailUrl && (
        <div
          style={{
            width: responsive.avatar.medium,
            height: responsive.avatar.medium,
            borderRadius: responsive.borderRadius.base,
            overflow: 'hidden',
            flexShrink: 0,
            cursor: 'pointer',
          }}
          onClick={() => onContentTap(transaction)}
        >
          <ContentPreview
            thumbnailUrl={thumbnailUrl}
            alt="Premium video"
          />
        </div>
      )}

      {isFromAdmin && <AdminUserAvatar userId={transaction.usersInfo.userId} />}
    </div>
  );
};
